import React, {PureComponent} from 'react';
import {AutoSizer, Column, Table} from 'react-virtualized';
import moment from 'moment-timezone';
import _ from 'lodash';

import {
    DEFAULT_PROPERTY_NAME_TO_COLUMN_NAME_MAP, DWELL_TIME_NAME_TO_COLUMN_MAP,
    POST_RECEIVE_ONLY_PROPERTY_NAME_TO_COLUMN_NAME_MAP,
    TRANSSHIPMENT_ONLY_PROPERTY_NAME_TO_COLUMN_NAME_MAP,
    VENDOR_RECEIVE_ONLY_PROPERTY_NAME_TO_COLUMN_NAME_MAP,
    WORKPOOL_ONLY_PROPERTY_NAME_TO_TO_COLUMN_MAP
} from './pojos/ItemListRecord';

import '../../../css/custom-scrollbar.scss';
import 'react-virtualized/styles.css';

import{
    SOURCE_WAREHOUSE_ID_PROPERTY_NAME,
    SOURCE_WAREHOUSE_ID_OR_SCSC_CODE_PROPERTY_NAME
} from '../pojos/PropertyNames';

const DANGER_DWELL_TIME_DURATION = moment.duration(2, 'day');
const WARNING_DWELL_TIME_DURATION = moment.duration(1, 'day');

const SORT_ASC = 'ASC';
const SORT_DESC = 'DESC';
const DWELL_TIME_COLUMN = _.head([...DWELL_TIME_NAME_TO_COLUMN_MAP.keys()]);
const NA_REALM = "na";
const EU_REALM = "eu";
const PROD_STAGE = "prod";
const GAMMA_STAGE = "gamma";
const BETA_STAGE = "beta";
const DOCKMASTER_PROD_HOST_BY_REALM = {
    [NA_REALM]: "https://prod-na.us-east-1.idx-website.aft.amazon.dev",
    [EU_REALM]: "https://prod-eu.eu-west-1.idx-website.aft.amazon.dev",
};
// DockMaster 3.0 has one non-prod stack per stage (NA only), so beta and gamma links ignore the realm.
const DOCKMASTER_NON_PROD_HOST_BY_STAGE = {
    [BETA_STAGE]: "https://dev.us-west-2.idx-website.aft.amazon.dev",
    [GAMMA_STAGE]: "https://gamma.us-east-1.idx-website.aft.amazon.dev",
};
const DOCKMASTER_EXTERNAL_NON_PROD_HOST_BY_STAGE = {
    [BETA_STAGE]: "https://idx.na.integ.aftx.amazonoperations.app",
    [GAMMA_STAGE]: "https://idx.gamma.na.integ.aftx.amazonoperations.app",
};

const ITEMLIST_FIELDS_UILINKS_ENUM={
    SCANNABLE_ID: "scannableId",
    CONTAINER_ID: "containerId",
    FNSKU: "fnSku",
    FCSKU: "fcSku",
    OUTER_SCANNABLE_ID: "outerScannableId",
    OUTER_OUTER_SCANNABLE_ID: "outerOuterScannableId",
    ISA_ID: "isaId",
    ISD_ID: "isdId",
    MANIFEST_ID: "manifestId",
    ASN_ID: "asnId"
}

export default class ItemListTable extends PureComponent {

    constructor(props) {
        super(props);

        this.state = {
            sortBy: DWELL_TIME_COLUMN,
            sortDirection: SORT_ASC
        };

        this.noRowsRenderer = this.noRowsRenderer.bind(this);
        this.sort = this.sort.bind(this);
    }

    render() {
        const sortedList = this.sortList();
        const columnDefinitions = this.buildColumnDefinitions();
        const columnWidths = this.computeColumnWidths(columnDefinitions);
        const totalColumnsWidth = columnDefinitions.reduce(
            (sum, {propertyName}) => sum + columnWidths.get(propertyName), 0);
        return (
            <div className="item-list-table-scroll">
                <AutoSizer>
                    {({height, width}) => (
                        <Table
                            ref="Table"
                            headerClassName="header-column h-100"
                            className={!this.props.isLoaded ? 'opaque-loading' : ''}
                            gridClassName="table-grid scrollable"
                            headerHeight={65}
                            noRowsRenderer={this.noRowsRenderer}
                            overscanRowCount={8}
                            rowClassName={ItemListTable.rowClassName}
                            rowHeight={60}
                            rowGetter={({index}) => sortedList[index]}
                            rowCount={this.props.itemListRecords.length}
                            sort={this.sort}
                            sortBy={this.state.sortBy}
                            sortDirection={this.state.sortDirection}
                            height={height}
                            width={Math.max(width, totalColumnsWidth)}
                        >
                            {columnDefinitions.map(({propertyName, columnName, isDwellTime}) =>
                                this.generateColumn(propertyName, columnName, isDwellTime, columnWidths.get(propertyName)))}
                        </Table>
                    )}
                </AutoSizer>
            </div>
        );
    }

    sortList() {
        const sortedList = _.sortBy(this.props.itemListRecords,
            item => item[this.state.sortBy === DWELL_TIME_COLUMN ?
                'dwellTime' :
                this.state.sortBy]);
        if (this.state.sortDirection === SORT_DESC) {
            sortedList.reverse();
        }
        return sortedList;
    }

    sort({sortBy, sortDirection}) {
        this.setState({sortBy, sortDirection});
    }

    noRowsRenderer() {
        return <div className="no-item-list-rows">{this.props.isLoaded ? 'No rows' : ''}</div>;
    }

     generateColumn(propertyName, columnName, isDwellTime, columnWidth) {
        return <Column
            key={columnName}
            className={`column-wrap ${isDwellTime ? 'h-100 w-100 mr-0' : ''}`}
            width={columnWidth}
            label={columnName}
            dataKey={propertyName}
            cellRenderer={({cellData, rowData}) => !isDwellTime ? this.getLinkForCellRender(cellData, rowData, propertyName) : ItemListTable.dwellTimeRenderer(cellData, rowData)}
        />
    }

    /**
     * Builds the ordered list of columns to render, applying the same selection rules used
     * in render(). Extracted so column widths can be pre-computed for sizing.
     */
    buildColumnDefinitions() {
        const definitions = [];
        const add = (propertyName, columnName, isDwellTime = false) =>
            definitions.push({propertyName, columnName, isDwellTime});

        for (const [propertyName, columnName] of DEFAULT_PROPERTY_NAME_TO_COLUMN_NAME_MAP.entries()) {
            if ((propertyName === 'locationId' && !this.locationIdEnabled())
                || (propertyName === 'locationName' && !this.locationNameEnabled())) {
                continue;
            }
            add(propertyName, columnName);
        }
        if (this.props.isPostReceiveSelected) {
            for (const [propertyName, columnName] of POST_RECEIVE_ONLY_PROPERTY_NAME_TO_COLUMN_NAME_MAP.entries()) {
                add(propertyName, columnName);
            }
        }
        const shipmentTypeColumnMap = this.props.isTransshipmentSelected
            ? TRANSSHIPMENT_ONLY_PROPERTY_NAME_TO_COLUMN_NAME_MAP
            : VENDOR_RECEIVE_ONLY_PROPERTY_NAME_TO_COLUMN_NAME_MAP;
        for (const [propertyName, columnName] of shipmentTypeColumnMap.entries()) {
            add(propertyName, columnName);
        }
        for (const [propertyName, columnName] of this.props.additionalPropertyNameToHeaderNameMap.entries()) {
            add(propertyName, columnName);
        }
        for (const [propertyName, columnName] of WORKPOOL_ONLY_PROPERTY_NAME_TO_TO_COLUMN_MAP.entries()) {
            add(propertyName, columnName);
        }
        for (const [propertyName, columnName] of DWELL_TIME_NAME_TO_COLUMN_MAP.entries()) {
            add(propertyName, columnName, true);
        }
        return definitions;
    }

    /**
     * Computes each column's pixel width to fit its widest content on a single line — the
     * header label or any cell value — measured with the table's actual font (no hard-coded
     * widths). The result is memoized per item-list data set. When the summed widths exceed
     * the viewport the table scrolls horizontally, so nothing is hidden or truncated.
     */
    computeColumnWidths(columnDefinitions) {
        // Cache is keyed on both the data set and the displayed column set: applying filters
        // can add/remove columns (e.g. additional columns) without changing itemListRecords,
        // and those new columns need their own measured width — otherwise columnWidths.get()
        // returns undefined for them, making totalColumnsWidth NaN and hiding every row.
        const columnsCacheKey = columnDefinitions.map(({propertyName}) => propertyName).join('|');
        if (this.columnWidthsCacheKey === this.props.itemListRecords
            && this.columnWidthsColumnsCacheKey === columnsCacheKey) {
            return this.columnWidthsCache;
        }
        const context = this.getTextMeasureContext();
        const CELL_HORIZONTAL_PADDING_PX = 24; // cell padding + room for the header sort indicator
        const FALLBACK_CHAR_WIDTH_PX = 8;      // only used when canvas measurement is unavailable (e.g. jsdom tests)
        const measureTextWidth = text =>
            context ? context.measureText(text).width : (text || '').length * FALLBACK_CHAR_WIDTH_PX;
        const widths = new Map();
        for (const {propertyName, columnName, isDwellTime} of columnDefinitions) {
            let maxTextWidth = measureTextWidth(columnName || '');
            if (!isDwellTime) {
                for (const record of this.props.itemListRecords) {
                    const value = record[propertyName];
                    if (!_.isNil(value)) {
                        maxTextWidth = Math.max(maxTextWidth, measureTextWidth(String(value)));
                    }
                }
            }
            widths.set(propertyName, Math.ceil(maxTextWidth) + CELL_HORIZONTAL_PADDING_PX);
        }
        this.columnWidthsCache = widths;
        this.columnWidthsCacheKey = this.props.itemListRecords;
        this.columnWidthsColumnsCacheKey = columnsCacheKey;
        return widths;
    }

    /**
     * Lazily creates a canvas 2D context configured with the table's font (item-list grid
     * uses font-size 0.8em) for measuring text width in pixels. Returns null in environments
     * without canvas support (e.g. jsdom tests); callers fall back to a character estimate.
     */
    getTextMeasureContext() {
        if (this.textMeasureContext === undefined) {
            const canvas = document.createElement('canvas');
            this.textMeasureContext = (canvas.getContext && canvas.getContext('2d')) || null;
        }
        if (this.textMeasureContext) {
            const rootFontSizePx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
            const fontFamily = getComputedStyle(document.body).fontFamily || 'sans-serif';
            this.textMeasureContext.font = `${(rootFontSizePx * 0.8).toFixed(2)}px ${fontFamily}`;
        }
        return this.textMeasureContext;
    }

     getLinkForCellRender(cellData, rowData, propertyName) {

        const realm = this.props.isEURealm ? EU_REALM : NA_REALM ;

        if ( propertyName === ITEMLIST_FIELDS_UILINKS_ENUM.SCANNABLE_ID ) {
            return ItemListTable.getLinkForFCResearch(rowData.warehouseId, cellData, realm);
        }
        else if ( propertyName === ITEMLIST_FIELDS_UILINKS_ENUM.CONTAINER_ID ) {
            return ItemListTable.getLinkForFCResearch(rowData.warehouseId, cellData, realm);
        }
        else if ( propertyName === ITEMLIST_FIELDS_UILINKS_ENUM.FNSKU ) {
            return ItemListTable.getLinkForFCResearch(rowData.warehouseId, cellData, realm);
        }
        else if ( propertyName === ITEMLIST_FIELDS_UILINKS_ENUM.FCSKU ) {
            return ItemListTable.getLinkForFCResearch(rowData.warehouseId, cellData, realm);
        }
        else if ( propertyName === ITEMLIST_FIELDS_UILINKS_ENUM.OUTER_SCANNABLE_ID ){
            return ItemListTable.getLinkForFCResearch(rowData.warehouseId, cellData, realm);
        }
        else if ( propertyName === ITEMLIST_FIELDS_UILINKS_ENUM.OUTER_OUTER_SCANNABLE_ID ){
            return ItemListTable.getLinkForFCResearch(rowData.warehouseId, cellData, realm);
        }
        else if ( propertyName === ITEMLIST_FIELDS_UILINKS_ENUM.ISA_ID ) {
            return ItemListTable.getLinkForDockAppointment(rowData.warehouseId, cellData, realm);
        }
        else if ( propertyName === ITEMLIST_FIELDS_UILINKS_ENUM.ISD_ID ) {
            return ItemListTable.getLinkForDockShipment(rowData.warehouseId, cellData, realm);
        }
        else if ( propertyName === ITEMLIST_FIELDS_UILINKS_ENUM.MANIFEST_ID ) {
            return ItemListTable.getLinkForManifestId(rowData.warehouseId, cellData, realm);
        }
        else if ( propertyName === ITEMLIST_FIELDS_UILINKS_ENUM.ASN_ID ) {
            return ItemListTable.getLinkForASNId(rowData.warehouseId, cellData, realm);
        }
        this.getConcatenatedString(cellData, propertyName)
        return cellData;
    }

    getConcatenatedString(cellData, propertyName) {
        return !_.isEmpty(cellData) && (_.isEqual(propertyName, SOURCE_WAREHOUSE_ID_PROPERTY_NAME) || _.isEqual(propertyName, SOURCE_WAREHOUSE_ID_OR_SCSC_CODE_PROPERTY_NAME)) ? cellData.toString() : cellData;
    }

    locationIdEnabled() {
        return this.props.customWarehouseConfigurations?.isLocationIdEnabled === 'true';
    }

    locationNameEnabled() {
        return this.props.customWarehouseConfigurations?.isLocationNameEnabled === 'true';
    }

    static dwellTimeRenderer(cellData, rowData) {
        return <div
            className={`${ItemListTable.highlightDwellTime(rowData.dwellTime)} justify-content-center d-flex flex-column h-100`}>
            {cellData}
        </div>;
    }

    static highlightDwellTime(dwellTimeMills) {
        if (dwellTimeMills > DANGER_DWELL_TIME_DURATION) {
            return 'table-danger';
        }
        if (dwellTimeMills > WARNING_DWELL_TIME_DURATION) {
            return 'table-warning';
        }
        return 'table-success';

    }

    static rowClassName({index}) {
        if (index < 0) {
            return "header-row";
        } else {
            return index % 2 === 0 ? "even-row" : "odd-row";
        }
    }

    static getLinkForFCResearch(warehouseId, cellData, realm)
    {
        let fcResearchEndpoint = `https://fcresearch-${realm}.aka.amazon.com`;
        if (ItemListTable.isMocRequest()) {
            fcResearchEndpoint = `https://qifcr.${realm}.aftx.amazonoperations.app`;
        }
        return (
                <div>
                    <a href={`${fcResearchEndpoint}/${warehouseId}/results?s=${cellData}`} target="_blank" rel="noreferrer">{cellData}</a>
                </div> );
    }

    static getLinkForDockAppointment(warehouseId, cellData, realm)
    {
        return (
            <div>
                <a href={`${ItemListTable.getDockMasterEndpoint(realm)}/en_US/${warehouseId}/appointment/view/${cellData}/appointmentDetail`} target="_blank" rel="noreferrer">{cellData}</a>
            </div> );
    }

    static getLinkForDockShipment(warehouseId, cellData, realm)
    {
        return (
            <div>
                <a href={`${ItemListTable.getDockMasterEndpoint(realm)}/en_US/${warehouseId}/shipment/${cellData}`} target="_blank" rel="noreferrer">{cellData}</a>
            </div> );
    }

    static getDockMasterEndpoint(realm)
    {
        const stage = ItemListTable.getStage();
        if (ItemListTable.isMocRequest()) {
            return stage === PROD_STAGE
                ? `https://idx.${realm}.aftx.amazonoperations.app`
                : DOCKMASTER_EXTERNAL_NON_PROD_HOST_BY_STAGE[stage];
        }
        return stage === PROD_STAGE ? DOCKMASTER_PROD_HOST_BY_REALM[realm] : DOCKMASTER_NON_PROD_HOST_BY_STAGE[stage];
    }

    static getLinkForManifestId(warehouseId, cellData, realm)
    {
        let aftTransshipmentHubEndpoint = `https://afttransshipmenthub-${realm}.aka.amazon.com`;
        if (ItemListTable.isMocRequest()) {
            aftTransshipmentHubEndpoint = `https://transshipmenthub.${realm}.aftx.amazonoperations.app`;
        }
        return (
            <div>
                <a href={`${aftTransshipmentHubEndpoint}/${warehouseId}/view-contents/${cellData}`} target="_blank" rel="noreferrer">{cellData}</a>
            </div> );
    }

    static getLinkForASNId(warehouseId, cellData, realm)
    {
        let unifiedPortalEndpoint = `https://unified-portal-${realm}.inbound-shipment-signals.scot.a2z.com`;
        if (ItemListTable.isMocRequest()) {
            unifiedPortalEndpoint = `https://unified-inbound.${realm}.aftx.amazonoperations.app`;
        }
        return (
            <div>
                <a href={`${unifiedPortalEndpoint}/#/asnsearch?pageType=detail&asnID=${cellData}&asnVersion=1`} target="_blank" rel="noreferrer">{cellData}</a>
            </div> );
    }

    static isMocRequest()
    {
        return window.location.hostname.includes('aft.amazonoperations.app') || window.location.hostname.includes('aftx.amazonoperations.app');
    }

    // Stage from the hostname across corp (beta./gamma.), AFT-X (.integ./.gamma.) and MoC (.beta.) hosts.
    static getStage()
    {
        const hostnameLabels = window.location.hostname.split('.');
        if (hostnameLabels.includes(GAMMA_STAGE)) {
            return GAMMA_STAGE;
        }
        if (hostnameLabels.includes(BETA_STAGE) || hostnameLabels.includes('integ')) {
            return BETA_STAGE;
        }
        return PROD_STAGE;
    }
}
